import { useMemo } from 'react';
import { AlertTriangle, CheckCircle2, Clock, Download, FileSignature } from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@solara/ui-shared';
import type { AuditEventDto, TddSubmissionDto } from '@solara/api-client';
import { useAuditEvents, useTddSubmissions } from '@solara/api-client/react';
import { saveCsv } from '../data/exports';
import { LoadingBlock, ErrorBanner } from '../components/QueryState';

export default function CompliancePage() {
  const { t } = useTranslation('compliance');
  const { t: tTs } = useTranslation('tickstar');
  const { locale } = useI18n();
  const { toast } = useToast();
  const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });
  const aedFmt = new Intl.NumberFormat(locale, { style: 'currency', currency: 'AED' });

  const tddQuery = useTddSubmissions();
  const auditQuery = useAuditEvents({ limit: 200 });

  // Backend ships `deadlineAt`/`submittedAt`/`occurredAt`/`actorEmail` — keep
  // the pre-existing field names the JSX reads (`deadlineAt`/`at`/`actor`) by
  // normalising once at the top of the page.
  const tddList = useMemo(() => {
    const list = tddQuery.data ?? [];
    return list.map((t) => ({
      ...t,
      deadlineAt: t.deadlineAt ?? (t as { deadline?: string }).deadline ?? new Date().toISOString(),
      submittedAt: t.submittedAt ?? null,
    }));
  }, [tddQuery.data]);
  const auditList = useMemo(() => {
    const list = auditQuery.data ?? [];
    return list.map((e) => {
      const ev = e as typeof e & { occurredAt?: string; actorEmail?: string };
      return {
        ...e,
        at: ev.occurredAt ?? e.at ?? new Date().toISOString(),
        actor: ev.actorEmail ?? e.actor ?? 'system',
      };
    });
  }, [auditQuery.data]);

  const stats = useMemo(() => {
    const now = Date.now();
    const c = { accepted: 0, pending: 0, failed: 0, dueSoon: 0, totalVat: 0 };
    for (const td of tddList) {
      if (td.status === 'ACCEPTED') c.accepted++;
      else if (td.status === 'SUBMITTED' || td.status === 'PENDING') c.pending++;
      else if (td.status === 'FAILED' || td.status === 'DEAD_LETTER') c.failed++;
      const dueIn = new Date(td.deadlineAt).getTime() - now;
      if (dueIn > 0 && dueIn < 3 * 86400_000 && td.status !== 'ACCEPTED') c.dueSoon++;
      // TddSubmissionDto carries no per-invoice VAT total — totalVat stays 0
      // unless we join through invoices, which the UI doesn't require here.
    }
    return c;
  }, [tddList]);

  function exportAudit() {
    if (auditList.length === 0) {
      toast({ title: t('exportToast', { count: 0 }), tone: 'info' });
      return;
    }
    saveCsv(
      `solara-audit-${new Date().toISOString().slice(0, 10)}.csv`,
      auditList.map((e) => ({
        when: e.at,
        actor: e.actor,
        action: e.action,
        resource: e.resource,
        ip: e.ip ?? '',
        metadata: JSON.stringify(e.metadata ?? null),
      })),
      ['when', 'actor', 'action', 'resource', 'ip', 'metadata'],
    );
    toast({ title: t('exportToast', { count: auditList.length }), tone: 'success' });
  }

  if (tddQuery.isLoading || auditQuery.isLoading) return <LoadingBlock />;
  if (tddQuery.error) return <ErrorBanner error={tddQuery.error} />;
  if (auditQuery.error) return <ErrorBanner error={auditQuery.error} />;

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
            <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 border border-amber-100 px-2 py-0.5 text-[10px] font-medium" title={tTs('brand.partnerLong')}>
              {tTs('pillars.tdd.title')}
            </span>
          </div>
          <p className="text-fg-muted text-sm">{t('subtitle')}</p>
        </div>
        <Button onClick={exportAudit} variant="outline" className="gap-2">
          <Download className="h-4 w-4" />
          {t('exportAudit')}
        </Button>
      </header>

      <section className="grid gap-3 sm:grid-cols-4">
        <Stat label={t('stats.accepted')} value={stats.accepted} icon={<CheckCircle2 className="h-4 w-4" />} tone="success" />
        <Stat label={t('stats.pending')} value={stats.pending} icon={<Clock className="h-4 w-4" />} tone="info" />
        <Stat label={t('stats.failed')} value={stats.failed} icon={<AlertTriangle className="h-4 w-4" />} tone="danger" />
        <Stat label={t('stats.vat')} rawValue={aedFmt.format(stats.totalVat)} icon={<FileSignature className="h-4 w-4" />} tone="brand" />
      </section>

      {stats.dueSoon > 0 ? (
        <Card className="rounded-2xl border-warning/30 bg-warning/5">
          <CardBody className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-warning shrink-0" />
            <div className="text-sm">{t('deadlineWarn', { count: stats.dueSoon })}</div>
          </CardBody>
        </Card>
      ) : null}

      <Tabs defaultValue="tdd">
        <TabsList>
          <TabsTrigger value="tdd">{t('tabs.tdd')}</TabsTrigger>
          <TabsTrigger value="audit">{t('tabs.audit')}</TabsTrigger>
        </TabsList>

        <TabsContent value="tdd">
          {tddList.length === 0 ? (
            <EmptyState icon={<FileSignature className="h-6 w-6" />} title={t('empty.tdd')} />
          ) : (
            <Card className="rounded-2xl">
              <CardBody>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-fg-subtle">
                        <th className="text-start font-medium pb-2">{t('tdd.id')}</th>
                        <th className="text-start font-medium pb-2">{t('tdd.status')}</th>
                        <th className="text-start font-medium pb-2">{t('tdd.submitted')}</th>
                        <th className="text-start font-medium pb-2">{t('tdd.deadline')}</th>
                        <th className="text-start font-medium pb-2">{t('tdd.ref')}</th>
                        <th className="text-start font-medium pb-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {tddList.map((td) => (
                        <TddRow key={td.id} tdd={td} dateFmt={dateFmt} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="audit">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle>{t('audit.title', { count: auditList.length })}</CardTitle>
            </CardHeader>
            <CardBody>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-fg-subtle">
                      <th className="text-start font-medium pb-2">{t('audit.when')}</th>
                      <th className="text-start font-medium pb-2">{t('audit.actor')}</th>
                      <th className="text-start font-medium pb-2">{t('audit.action')}</th>
                      <th className="text-start font-medium pb-2">{t('audit.resource')}</th>
                      <th className="text-start font-medium pb-2">{t('audit.ip')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {auditList.map((e: AuditEventDto) => (
                      <tr key={e.id}>
                        <td className="py-2 num text-xs">{dateFmt.format(new Date(e.at))}</td>
                        <td className="py-2">
                          <div className="text-xs">{e.actor}</div>
                        </td>
                        <td className="py-2"><code className="text-xs">{e.action}</code></td>
                        <td className="py-2 text-xs">
                          <code className="text-[10px]">{e.resource}</code>
                        </td>
                        <td className="py-2 text-xs num">{e.ip ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TddRow({
  tdd,
  dateFmt,
}: {
  tdd: TddSubmissionDto;
  dateFmt: Intl.DateTimeFormat;
}) {
  const tone =
    tdd.status === 'ACCEPTED'
      ? 'success'
      : tdd.status === 'SUBMITTED'
      ? 'info'
      : tdd.status === 'FAILED' || tdd.status === 'DEAD_LETTER'
      ? 'danger'
      : 'warning';
  return (
    <tr>
      <td className="py-2"><code className="text-[10px]">{tdd.id.slice(0, 8)}…</code></td>
      <td className="py-2">
        <Badge tone={tone}>{tdd.status}</Badge>
      </td>
      <td className="py-2 num text-xs">{tdd.submittedAt ? dateFmt.format(new Date(tdd.submittedAt)) : '—'}</td>
      <td className="py-2 num text-xs">{dateFmt.format(new Date(tdd.deadlineAt))}</td>
      <td className="py-2"><code className="text-[10px]">{tdd.emaraConfirmationId ?? '—'}</code></td>
      <td className="py-2 text-xs text-danger">{tdd.errorMessage ?? ''}</td>
    </tr>
  );
}

function Stat({
  label,
  value,
  rawValue,
  icon,
  tone,
}: {
  label: string;
  value?: number;
  rawValue?: string;
  icon: React.ReactNode;
  tone: 'success' | 'danger' | 'info' | 'brand';
}) {
  const accent = {
    success: 'text-success bg-emerald-50',
    danger: 'text-danger bg-red-50',
    info: 'text-info bg-cyan-50',
    brand: 'text-brand-600 bg-brand-50',
  }[tone];
  return (
    <Card className="rounded-2xl">
      <CardBody className="p-4 flex items-center gap-3">
        <div className={`h-10 w-10 grid place-items-center rounded-xl shrink-0 ${accent}`}>{icon}</div>
        <div>
          <div className="text-xs uppercase tracking-wide text-fg-subtle">{label}</div>
          <div className="text-xl font-semibold num">{rawValue ?? value}</div>
        </div>
      </CardBody>
    </Card>
  );
}
